import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, of, EMPTY } from 'rxjs';
import { environment } from '@env/environment';
import {
  Cart,
  CartItem,
  AddToCartRequest,
  UpdateCartItemRequest,
  CheckoutResponse,
  InventoryPackageSummary,
  InventoryItem,
  Project,
  AssignToProjectRequest,
  UpdateProductActivationsRequest,
  PackageAssignment
} from '../models/cart.model';
import { AuthService } from './auth.service';
import { LoginPromptService } from './login-prompt.service';

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private readonly CART_API_URL = `${environment.apiUrl}/cart`;
  private readonly INVENTORY_API_URL = `${environment.apiUrl}/inventory`;

  // Reactive cart state using signals
  private _cart = signal<Cart | null>(null);
  private _loading = signal<boolean>(false);
  private _error = signal<string | null>(null);

  // Public readonly signals
  readonly cart = this._cart.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  // Computed values
  readonly cartItemCount = computed(() => this._cart()?.totalItems ?? 0);
  readonly cartTotalPrice = computed(() => this._cart()?.totalPrice ?? 0);
  readonly hasItems = computed(() => (this._cart()?.totalItems ?? 0) > 0);

  private readonly authService = inject(AuthService);
  private readonly loginPrompt = inject(LoginPromptService);
  private readonly router = inject(Router);

  constructor(private http: HttpClient) {}

  /**
   * Warenkorb-Aktionen setzen eine Anmeldung voraus. Ohne Anmeldung wird der
   * globale Hinweisdialog geöffnet statt ein 401 auszulösen.
   */
  private ensureAuthenticated(): boolean {
    if (this.authService.isAuthenticated()) {
      return true;
    }
    this.loginPrompt.show('cart', this.router.url);
    return false;
  }

  // ============ CART OPERATIONS ============

  /**
   * Load the current user's cart
   */
  loadCart(): void {
    if (!this.authService.isAuthenticated()) {
      this._cart.set(null);
      return;
    }

    this._loading.set(true);
    this._error.set(null);

    this.http.get<Cart>(this.CART_API_URL).pipe(
      tap(cart => {
        this._cart.set(cart);
        this._loading.set(false);
      }),
      catchError(err => {
        // 404 means no cart yet, which is fine
        if (err.status === 404) {
          this._cart.set({ id: '', userId: '', items: [], totalItems: 0, totalPrice: 0, updatedAt: new Date().toISOString() });
        } else {
          this._error.set('Failed to load cart');
          console.error('Error loading cart:', err);
        }
        this._loading.set(false);
        return of(null);
      })
    ).subscribe();
  }

  /**
   * Add a package to the cart
   */
  addToCart(packageId: string, quantity: number = 1): Observable<Cart> {
    if (!this.ensureAuthenticated()) {
      return EMPTY;
    }
    const request: AddToCartRequest = { packageId, quantity };
    return this.http.post<Cart>(`${this.CART_API_URL}/items`, request).pipe(
      tap(cart => this._cart.set(cart))
    );
  }

  /**
   * Add a product to the cart
   */
  addProductToCart(productId: string, quantity: number = 1): Observable<Cart> {
    if (!this.ensureAuthenticated()) {
      return EMPTY;
    }
    const request = { productId, quantity };
    return this.http.post<Cart>(`${this.CART_API_URL}/items/product`, request).pipe(
      tap(cart => this._cart.set(cart))
    );
  }

  /**
   * Update quantity of a cart item
   */
  updateCartItem(itemId: string, quantity: number): Observable<Cart> {
    const request: UpdateCartItemRequest = { quantity };
    return this.http.put<Cart>(`${this.CART_API_URL}/items/${itemId}`, request).pipe(
      tap(cart => this._cart.set(cart))
    );
  }

  /**
   * Remove an item from the cart
   */
  removeFromCart(itemId: string): Observable<Cart> {
    return this.http.delete<Cart>(`${this.CART_API_URL}/items/${itemId}`).pipe(
      tap(cart => this._cart.set(cart))
    );
  }

  /**
   * Clear all items from the cart
   */
  clearCart(): Observable<void> {
    return this.http.delete<void>(this.CART_API_URL).pipe(
      tap(() => this._cart.set({ id: '', userId: '', items: [], totalItems: 0, totalPrice: 0, updatedAt: new Date().toISOString() }))
    );
  }

  /**
   * Checkout the cart - creates purchase and moves items to inventory
   */
  checkout(payload?: any): Observable<CheckoutResponse> {
    return this.http.post<CheckoutResponse>(`${this.CART_API_URL}/checkout`, payload ?? {}).pipe(
      tap(() => {
        // Clear local cart after successful checkout
        this._cart.set({ id: '', userId: '', items: [], totalItems: 0, totalPrice: 0, updatedAt: new Date().toISOString() });
      })
    );
  }

  /**
   * Validate a voucher code against the current cart.
   */
  validateVoucher(code: string): Observable<any> {
    return this.http.post<any>(`${this.CART_API_URL}/voucher/validate`, { code });
  }

  // ============ INVENTORY OPERATIONS ============

  /**
   * Get user's inventory (purchased packages), grouped by package type
   */
  getInventory(): Observable<InventoryPackageSummary[]> {
    return this.http.get<InventoryPackageSummary[]>(this.INVENTORY_API_URL);
  }

  /**
   * Get a specific inventory item
   */
  getInventoryItem(purchaseItemId: string): Observable<InventoryItem> {
    return this.http.get<InventoryItem>(`${this.INVENTORY_API_URL}/${purchaseItemId}`);
  }

  /**
   * Get user's projects (synced from homeXpert)
   */
  getProjects(): Observable<Project[]> {
    return this.http.get<Project[]>(`${this.INVENTORY_API_URL}/projects`);
  }

  /**
   * Sync projects from homeXpert
   */
  syncProjects(): Observable<Project[]> {
    return this.http.post<Project[]>(`${this.INVENTORY_API_URL}/projects/sync`, {});
  }

  /**
   * Assign an inventory item (package) to a project
   * This is permanent and cannot be undone!
   */
  assignToProject(purchaseItemId: string, projectId: string, notes?: string): Observable<PackageAssignment> {
    const request: AssignToProjectRequest = { purchaseItemId, projectId, notes };
    return this.http.post<PackageAssignment>(`${this.INVENTORY_API_URL}/assign`, request);
  }

  /**
   * Update product activations for an assigned package
   */
  updateProductActivations(assignmentId: string, request: UpdateProductActivationsRequest): Observable<PackageAssignment> {
    return this.http.put<PackageAssignment>(
      `${this.INVENTORY_API_URL}/assignments/${assignmentId}/activations`,
      request
    );
  }

  /**
   * Get packages assigned to a specific project
   */
  getProjectAssignments(projectId: string): Observable<PackageAssignment[]> {
    return this.http.get<PackageAssignment[]>(`${this.INVENTORY_API_URL}/projects/${projectId}/assignments`);
  }
}
